// Quick add — paste a week's bookings out of Excel, fix anything flagged, import.
// Built for the weekly routine: copy the name/date columns, paste, check, done.
import React, { useState } from 'react';
import { api, ApiError } from '../api';
import { ErrorBox, Field, DateField } from '../ui';
import { useLookup } from '../crud';
import { fromAus, toAus, ATTENDANCE_STATUSES } from '../util';

type Row = {
  first_name: string; last_name: string;
  arrival: string | null; departure: string | null;
  // filled in by the server check
  row?: number; person_id?: number | null; person_state?: string;
  candidates?: { id: number; label: string }[];
  existing_period?: { id: number; check_in: string; check_out: string } | null;
  same_as_row?: number | null;
  suggested_action?: 'create' | 'update' | 'skip';
  action?: 'create' | 'update' | 'skip';   // user override
  errors?: string[]; warnings?: string[]; ok?: boolean;
};

const asPayload = (r: Row) => ({
  first_name: r.first_name, last_name: r.last_name,
  arrival: r.arrival, departure: r.departure, person_id: r.person_id ?? null,
  action: r.action ?? undefined
});

const EXAMPLE = `First Name\tLast Name\tArrival Date\tDeparture Date
Simon\tKing\t3/09/2026\t13/09/2026
Rowan\tJoss\t9/09/2026\t27/09/2026`;

// Accepts tab-separated (an Excel copy) or comma-separated text.
function parsePaste(text: string): Row[] {
  const out: Row[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const cells = (line.includes('\t') ? line.split('\t') : line.split(',')).map(c => c.trim().replace(/^"|"$/g, ''));
    if (cells.length < 2) continue;
    // Skip a header row if one came along with the copy.
    if (/first\s*name/i.test(cells[0]) || /last\s*name/i.test(cells[1] || '')) continue;
    out.push({
      first_name: cells[0] || '',
      last_name: cells[1] || '',
      arrival: fromAus(cells[2] || ''),
      departure: fromAus(cells[3] || '')
    });
  }
  return out;
}

export default function QuickAdd() {
  const projects = useLookup('projects');
  const workAreas = useLookup('work_areas');
  const bookings = useLookup('bookings', (b: any) =>
    `${b.booking_reference || '#' + b.id} · ${toAus(b.check_in)}–${toAus(b.check_out)} · ${b.num_rooms} room(s)`);

  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [status, setStatus] = useState('confirmed');
  const [workAreaId, setWorkAreaId] = useState<number | ''>('');
  const [bookingId, setBookingId] = useState<number | ''>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const activeProject = projectId === '' ? (projects.rows[0]?.id ?? '') : projectId;

  const check = async (source?: Row[]) => {
    const parsed = source || parsePaste(text);
    if (!parsed.length) { setErrors(['Could not read any rows. Paste at least a name and two dates per line.']); return; }
    if (activeProject === '') { setErrors(['Choose a project first.']); return; }
    setBusy(true); setErrors([]); setDone(null);
    try {
      const r = await api('/quick-add/check', { method: 'POST', body: { project_id: activeProject, rows: parsed } });
      // Keep any action the user has already chosen for a row; otherwise take the
      // server's suggestion.
      setRows(r.rows.map((row: Row, i: number) => ({
        ...row,
        action: parsed[i]?.action ?? row.suggested_action
      })));
      setSummary(r.summary);
    } catch (e: any) {
      setErrors(e instanceof ApiError && e.errors.length ? e.errors : [e.message]);
    } finally { setBusy(false); }
  };

  const editRow = (i: number, patch: Partial<Row>) => {
    if (!rows) return;
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    setRows(next);
    check(next.map(asPayload));
  };

  const commit = async () => {
    if (!rows) return;
    setBusy(true); setErrors([]);
    try {
      const r = await api('/quick-add/commit', {
        method: 'POST',
        body: {
          project_id: activeProject, status,
          work_area_id: workAreaId || null,
          booking_id: bookingId || null,
          rows: rows.map(asPayload)
        }
      });
      setDone([
        r.created.attendance ? `${r.created.attendance} new stay(s) added` : '',
        r.created.updated ? `${r.created.updated} existing stay(s) updated` : '',
        r.created.skipped ? `${r.created.skipped} row(s) skipped` : '',
        r.created.people ? `${r.created.people} new personnel record(s) created` : '',
        r.created.allocations ? `${r.created.allocations} assigned to the booking` : ''
      ].filter(Boolean).join(', ') + '.');
      setRows(null); setSummary(null); setText('');
    } catch (e: any) {
      setErrors(e instanceof ApiError && e.errors.length ? e.errors : [e.message]);
      if (e instanceof ApiError && (e as any).rows) setRows((e as any).rows);
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <div className="page-head"><h2>Quick add bookings</h2></div>

      <div className="panel">
        <p className="muted" style={{ marginTop: 0 }}>
          Copy the columns out of your accommodation spreadsheet — <strong>First Name, Last Name,
          Arrival Date, Departure Date</strong>, in that order — and paste them below. A header row
          is fine, it gets ignored. Dates can be 3/09/2026 or 03/09/2026.
        </p>
        <textarea
          className="input" rows={8} value={text}
          style={{ fontFamily: 'Consolas, monospace' }}
          placeholder={EXAMPLE}
          onChange={e => setText(e.target.value)}
        />
        <div className="form-grid" style={{ marginTop: 12 }}>
          <Field label="Project *">
            <select className="input" value={activeProject} onChange={e => setProjectId(Number(e.target.value))}>
              {projects.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Status to record" hint="These are already booked, so 'confirmed' is usually right.">
            <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
              {ATTENDANCE_STATUSES.filter(s => ['planned', 'tentative', 'confirmed', 'accommodation_booked'].includes(s))
                .map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Work area (optional)">
            <select className="input" value={workAreaId} onChange={e => setWorkAreaId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">—</option>
              {workAreas.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Attach to booking (optional)" hint="Creates the room allocation too, so confirmation numbers can be added.">
            <select className="input" value={bookingId} onChange={e => setBookingId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">— don't attach —</option>
              {bookings.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="btn-row form-actions">
          <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => check()}>
            {busy ? 'Checking…' : 'Check rows'}
          </button>
          {text.trim() === '' && (
            <button className="btn btn-ghost btn-sm" onClick={() => setText(EXAMPLE)}>paste an example</button>
          )}
        </div>
      </div>

      <ErrorBox error={errors} onDismiss={() => setErrors([])} />
      {done && (
        <div className="panel" style={{ borderLeft: '4px solid var(--olive)' }}>
          <strong>{done}</strong>{' '}
          <a href="#/schedule">Open the booking schedule →</a>{' '}
          <a href="#/timeline" style={{ marginLeft: 10 }}>See the timeline →</a>
        </div>
      )}

      {rows && summary && (
        <div className="panel">
          <h3>
            Preview — {summary.ready} of {summary.total} ready
            {summary.blocked ? `, ${summary.blocked} need fixing` : ''}
          </h3>
          <p className="muted">
            {summary.will_create > 0 && <><strong>{summary.will_create}</strong> new stay(s). </>}
            {summary.will_update > 0 && <><strong>{summary.will_update}</strong> existing stay(s) will be <strong>updated</strong> rather than duplicated. </>}
            {summary.will_skip > 0 && <>{summary.will_skip} row(s) will be skipped. </>}
            {summary.new_people > 0 && <>{summary.new_people} name(s) aren't on file yet and will be created as new personnel. </>}
            Check the <strong>What will happen</strong> column, and change any row you disagree with.
          </p>
          {summary.will_update > 0 && (
            <p className="muted">
              Note: when an existing stay's dates change, any booking already confirmed
              against it is cleared, because the provider confirmed the old dates. It will
              reappear on the booking schedule as a revision for the provider to action.
            </p>
          )}
          <div className="table-scroll" style={{ maxHeight: '55vh' }}>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>First Name</th><th>Last Name</th>
                  <th>Arrival</th><th>Departure</th><th>Person</th>
                  <th>What will happen</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={r.ok === false ? { background: 'color-mix(in srgb, var(--danger) 12%, transparent)' } : undefined}>
                    <td>{i + 1}</td>
                    <td><input className="input" style={{ minWidth: 110 }} value={r.first_name}
                      onChange={e => editRow(i, { first_name: e.target.value })} /></td>
                    <td><input className="input" style={{ minWidth: 110 }} value={r.last_name}
                      onChange={e => editRow(i, { last_name: e.target.value })} /></td>
                    <td><DateField value={r.arrival} hideHint onChange={v => editRow(i, { arrival: v })} /></td>
                    <td><DateField value={r.departure} hideHint onChange={v => editRow(i, { departure: v })} /></td>
                    <td>
                      {r.person_state === 'matched' && <span className="chip" style={{ background: 'var(--olive)' }}>on file</span>}
                      {r.person_state === 'new' && (
                        r.same_as_row
                          ? <span className="chip" style={{ background: '#5f7a81' }}>same as row {r.same_as_row}</span>
                          : <span className="chip" style={{ background: '#c9a227' }}>will create</span>
                      )}
                      {r.person_state === 'ambiguous' && (
                        <select className="input" style={{ minWidth: 150 }} value={r.person_id ?? ''}
                          onChange={e => editRow(i, { person_id: e.target.value === '' ? null : Number(e.target.value) })}>
                          <option value="">— pick which —</option>
                          {(r.candidates || []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ minWidth: 190 }}>
                      {r.existing_period ? (
                        <>
                          <select className="input" value={r.action ?? 'create'}
                            onChange={e => editRow(i, { action: e.target.value as any })}>
                            <option value="update">Update the existing stay</option>
                            <option value="create">Add as a second stay</option>
                            <option value="skip">Skip this row</option>
                          </select>
                          {r.action === 'update' && (
                            <div className="muted" style={{ whiteSpace: 'normal', marginTop: 2 }}>
                              {toAus(r.existing_period.check_in)}–{toAus(r.existing_period.check_out)}
                              {' → '}
                              <strong>{toAus(r.arrival)}–{toAus(r.departure)}</strong>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="muted">Add as a new stay</span>
                      )}
                    </td>
                    <td style={{ maxWidth: 300, whiteSpace: 'normal' }}>
                      {(r.errors || []).map((m, k) => (
                        <div key={'e' + k} style={{ color: 'var(--danger)', fontWeight: 600 }}>{m}</div>
                      ))}
                      {(r.warnings || []).map((m, k) => (
                        <div key={'w' + k} className="muted">⚠ {m}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="btn-row form-actions">
            <button className="btn btn-primary" disabled={busy || summary.blocked > 0} onClick={commit}>
              {busy ? 'Importing…' : `Apply ${rows.filter(r => (r.action ?? r.suggested_action) !== 'skip').length} row(s)`}
            </button>
            <button className="btn" onClick={() => { setRows(null); setSummary(null); }}>Cancel</button>
            {summary.blocked > 0 && (
              <span style={{ color: 'var(--danger)' }}>
                Fix the {summary.blocked} highlighted row(s) first — nothing imports until every row is valid.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
